<script setup lang="ts">
import { mdiClose, mdiHeart } from "@mdi/js";
import FilterExpandable from "./FilterExpandable.vue";
import NavIcon from "~/components/layout/Nav/NavIcon.vue";
import Checkbox from "~/components/ui/Form/Checkbox.vue";
import { useBetbook } from "~/store/betbook";
import { ISport } from "~/types/sports/sport";
import SubTabs from "~/components/ui/SubTabs.vue";
import t from "~/utils/translate";
import Icon from "~/components/ui/Icon.vue";
import { useAuth } from "~/store/auth";

interface Props {
	topLeagues?: ISport["topLeagues"];
	filter: {
		regionAndLeagueIds: { regionId: string; leagueId: string }[] | undefined;
	};
}

const tab = ref("all");
const store = useBetbook();
const auth = useAuth();
const props = defineProps<Props>();
const emits = defineEmits(["update-regions", "remove-region"]);

const hasTopLeaguesFilter = computed(() => {
	if (!props.topLeagues) return false;
	const flatTopLeagues = props.topLeagues.map((x) => {
		return `${x.regionId}_${x.leagueId}`;
	});
	const flatActiveLeagues = props.filter.regionAndLeagueIds?.map((x) => {
		return `${x.regionId}_${x.leagueId}`;
	});

	let matched = 0;
	flatTopLeagues.map((l) => {
		if (flatActiveLeagues?.includes(l)) matched++;
	});

	return matched === flatTopLeagues.length;
});

const hasFavouritesFilter = computed(() => {
	if (!store.favourites.league.length) return false;
	const flatFavouriteLeagues = store.favourites.league.map((x) => {
		return x.id;
	});
	const flatActiveLeagues = props.filter.regionAndLeagueIds?.map((x) => {
		return `${x.regionId}_${x.leagueId}`;
	});

	let matched = 0;
	flatFavouriteLeagues.map((l) => {
		if (flatActiveLeagues?.includes(l)) matched++;
	});

	return matched === flatFavouriteLeagues.length;
});

const sortedLeagues = computed(() => {
	const regionArray = Array.from(store.regions.values());
	const leaguesBySortIndex = regionArray.filter((x) => x.sortIndex !== 999);
	const leaguesAlphabetically = regionArray.filter((x) => x.sortIndex === 999).sort((a, b) => a.name.localeCompare(b.name));

	return [...leaguesBySortIndex, ...leaguesAlphabetically];
});

const getSportInitialState = (index: number) => {
	return hasTopLeaguesFilter.value && store.favourites.league.length <= 0 && index === 0 ? true : false;
};
</script>

<template>
	<div class="relative">
		<SubTabs
			class="sticky top-0 left-0 z-10 bg-light-200 dark:bg-dark-800"
			:initial-tab="tab"
			@on-update="(val) => (tab = val)"
			:tabs="[
				{ name: t('all-leagues'), value: 'all' },
				{
					name: t('selection'),
					value: 'selection',
					count: filter?.regionAndLeagueIds?.filter((l) => l.leagueId !== 'top-7-leagues' && store.regions.get(l.regionId))?.length || 0,
				},
			]"
		/>
		<div v-if="tab === 'all'">
			<!-- Favourites -->
			<FilterExpandable v-if="auth.loggedIn && store.favourites.league.length > 0" :initial-state="true" :expandable="true">
				<template #header>
					<div class="flex gap-1">
						<Icon :width="24" :height="24" :path="mdiHeart" :alt="t('favourites')" class="flex-shrink-0 fill-identity" />
						<strong>{{ t("favourites") }}</strong>
					</div>
				</template>
				<div class="grid grid-cols-1">
					<div class="flex items-center p-2 border-b border-r border-light-400 dark:border-dark-500">
						<Checkbox
							:model-value="!!hasFavouritesFilter"
							@update:model-value="
							(val) => {
								const action = val === true ? 'update-regions' : 'remove-region';
								const data: { regionId: string, leagueId: string }[] = [];
								store.favourites.league.map(l => {
									const split = l.id.split('_')
									data.push({ regionId: split[0], leagueId: split[1] })
								})
								data.forEach((d) => {
									emits(action, d);
								});
							}
						"
							:label="t('your-favourites')"
						/>
					</div>
				</div>
			</FilterExpandable>
			<!-- Top Leagues -->
			<FilterExpandable v-if="topLeagues && topLeagues.length > 0" :initial-state="true" :expandable="true">
				<template #header>
					<div class="flex gap-1">
						<DataFreeGuard>
							<NavIcon
								:width="24"
								:height="24"
								:src="`/icons/favorite.svg`"
								:alt="'Top Leagues'"
								fallback="/icons/regions-leagues-flags/default-circular.svg"
								class="flex-shrink-0"
							/>
						</DataFreeGuard>
						<strong>{{ t("top-leagues") }}</strong>
					</div>
				</template>
				<div class="grid grid-cols-1">
					<div class="flex items-center p-2 border-b border-r border-light-400 dark:border-dark-500">
						<Checkbox
							:model-value="!!hasTopLeaguesFilter"
							@update:model-value="
							(val) => {
								const action = val === true ? 'update-regions' : 'remove-region';
								const data: { regionId: string, leagueId: string }[] = [];
								topLeagues?.map((l) => {
									l.shouldDisplay && data.push({ regionId: l.regionId, leagueId: l.leagueId });
								});
								data.forEach((d) => {
									emits(action, d);
								});
							}
						"
							:label="`${t('top-leagues-count', { variables: [{ key: 'count', value: topLeagues.length || 0 }] })} (${
								topLeagues && topLeagues.length > 0 && topLeagues.map((l) => l.shouldDisplay && l.defaultName).join(', ')
							})`"
						/>
					</div>
				</div>
			</FilterExpandable>
			<FilterExpandable v-for="(region, idx) in sortedLeagues" :key="region.name" :initial-state="getSportInitialState(idx)" :expandable="true">
				<template #header>
					<div class="flex gap-1">
						<DataFreeGuard>
							<div class="w-6 h-6">
								<NavIcon
									:width="24"
									:height="24"
									:src="`/icons/regions-leagues-flags/${region.regionId.toLowerCase()}-circular.svg`"
									:alt="region.name"
									fallback="/icons/regions-leagues-flags/default-circular.svg"
									class="flex-shrink-0"
								/>
							</div>
						</DataFreeGuard>
						<strong class="whitespace-nowrap">{{ region.name }}</strong>
					</div>
				</template>
				<div class="grid grid-cols-2">
					<div
						class="flex items-center p-2 border-b border-r border-light-400 dark:border-dark-500"
						v-for="league in region.leagues"
						:key="`${league.leagueId}-${region.regionId}`"
					>
						<Checkbox
							:model-value="
								filter.regionAndLeagueIds && filter.regionAndLeagueIds.find((x) => x.leagueId === league.leagueId && x.regionId === region.regionId)
									? true
									: false
							"
							:id="`${league.leagueId}-${region.regionId}`"
							@update:model-value="
								(val) => {
									if (val === true) emits('update-regions', { regionId: region.regionId, leagueId: league.leagueId });
									else emits('remove-region', { regionId: region.regionId, leagueId: league.leagueId });
								}
							"
							:label="league.name"
						/>
					</div>
					<div v-if="region.leagues.length % 2" class="w-full border-b border-r border-light-400 dark:border-dark-500" />
				</div>
			</FilterExpandable>
		</div>
		<div v-else-if="tab === 'selection'">
			<div
				v-if="
					!filter.regionAndLeagueIds ||
					filter?.regionAndLeagueIds.filter((l) => l.leagueId !== 'top-7-leagues' && store.regions.get(l.regionId))?.length <= 0
				"
				class="flex items-center justify-center p-2 text-xs"
			>
				{{ t("no-selection-available") }}
			</div>
			<div
				v-else
				v-for="selection in filter.regionAndLeagueIds.filter((l) => l.leagueId !== 'top-7-leagues' && store.regions.get(l.regionId))"
				:key="`selection-${selection.leagueId}-${selection.regionId}`"
				class="relative w-full p-2 pr-10 text-xs border-b border-b-light-400 bg-light-50 dark:bg-dark-600 dark:border-b-dark-500"
			>
				<strong>{{ store.regions.get(selection.regionId)?.defaultName }} </strong>,
				{{ store.regions.get(selection.regionId)?.leagues.find((x) => x.leagueId === selection.leagueId)?.defaultName }}
				<UiIcon
					@click="emits('remove-region', { regionId: selection.regionId, leagueId: selection.leagueId })"
					:path="mdiClose"
					class="absolute w-5 h-5 -translate-y-1/2 cursor-pointer right-2 top-1/2 dark:fill-light-50"
				/>
			</div>
		</div>
	</div>
</template>
